import React, {
	useState,
	useCallback,
	useMemo,
	useRef,
	useEffect,
} from 'react';
import { useSelector } from 'react-redux';
import { Avatar, Badge, Select, Spin, Tooltip, Typography } from 'antd';
import { useQuery, gql } from '@apollo/client';
import { ReactComponent as RecipientIcon } from 'src/assets/svg/recipientOne.svg';
import { debounce } from 'lodash';

const { Text } = Typography;
const QUERY_ACCOUNT_CLAIMS_WITH_SEARCH = gql`
	query QueryAccountClaimsWithSearch(
		$companyId: ID!
		$after: String
		$first: Int
		$searchInput: String
	) {
		queryAccountClaimsWithSearch(
			companyId: $companyId
			after: $after
			first: $first
			searchInput: $searchInput
		) {
			totalCount
			pageInfo {
				startCursor
				hasPreviousPage
				hasNextPage
				endCursor
			}
			accountClaims {
				userId
				user {
					id
					subCompanyId
					emailAddress
					departmentId
					cognitoId
				}
				title
				subCompany
				relevanceScore
				lastName
				id
				firstName
				employeeId
				emailAddress
				department
				companyId
			}
		}
	}
`;

const useAccountClaimsSelect = (companyId) => {
	const currentUser = useSelector((state) => state.user.currentUser);
	const [searchValue, setSearchValue] = useState('');
	const [debouncedSearchValue, setDebouncedSearchValue] = useState('');

	const debouncedSetSearch = useMemo(
		() =>
			debounce((value) => {
				setDebouncedSearchValue(value);
			}, 200),
		[]
	);

	const { data, loading, error } = useQuery(QUERY_ACCOUNT_CLAIMS_WITH_SEARCH, {
		variables: {
			companyId,
			first: 50,
			after: null,
			searchInput: debouncedSearchValue,
		},
		skip: !debouncedSearchValue || debouncedSearchValue.trim().length === 0,
		errorPolicy: 'all',
	});

	const selectOptions = useMemo(() => {
		if (!data?.queryAccountClaimsWithSearch?.accountClaims) {
			return [];
		}

		const currentUserEmail = currentUser?.emailAddress?.toLowerCase();
		const claims = data.queryAccountClaimsWithSearch.accountClaims.filter(
			(claim) =>
				!currentUserEmail ||
				claim.emailAddress?.toLowerCase() !== currentUserEmail
		);

		const subCompanies = [
			...new Set(claims.map((claim) => claim.subCompany)),
		].sort();

		const groupedOptions = subCompanies.map((subCompany) => {
			const companyOptions = claims
				.filter((claim) => claim.subCompany === subCompany)
				.map((claim) => {
					const firstInitial = claim.firstName?.charAt(0)?.toUpperCase() || '';
					const lastInitial = claim.lastName?.charAt(0)?.toUpperCase() || '';
					const initials = firstInitial + lastInitial;
					const displayText = `${claim.firstName} ${claim.lastName} - ${claim.emailAddress} - ${claim.title}`;

					return {
						label: displayText,
						value: claim.id,
						claim: claim,
						initials: initials,
					};
				});

			return {
				label: subCompany || 'Other',
				options: companyOptions,
			};
		});

		return groupedOptions;
	}, [data, currentUser]);

	const handleSearch = useCallback(
		(value) => {
			setSearchValue(value);
			if (value && value.trim().length > 0) {
				debouncedSetSearch(value.trim());
			} else {
				debouncedSetSearch('');
				setDebouncedSearchValue('');
			}
		},
		[debouncedSetSearch]
	);

	return {
		options: selectOptions,
		loading,
		error,
		searchValue,
		handleSearch,
	};
};

const AccountClaimsSelect = ({
	companyId,
	mode = 'multiple',
	value: valueFromProps,
	fullValue,
	setFullValue,
	onChange,
	badgeAssignmentRecipients,
	setBadgeAssignmentRecipients,
	style = { width: '100%' },
	...props
}) => {
	const { options, loading, error, searchValue, handleSearch } =
		useAccountClaimsSelect(companyId);
	const [historicOptions, setHistoricOptions] = useState([]);
	const [dropdownOpen, setDropdownOpen] = useState(false);
	const placeholder = (
		<div style={{ width: 120, opacity: dropdownOpen ? 0.1 : 1 }}>
			<RecipientIcon
				style={{
					height: 18,
					width: 18,
					marginRight: '4px',
					display: 'inline-block',
				}}
			/>
			<span style={{ verticalAlign: 'middle' }}>Select Recipients</span>
		</div>
	);
	const selectRef = useRef(null);

	const scrollToEnd = useCallback(() => {
		if (selectRef.current) {
			const selectElement =
				selectRef.current.nativeElement || selectRef.current;
			const selectorElement =
				selectElement?.querySelector?.('.ant-select-selection-overflow') ||
				document.querySelector(
					'.horizontal-tags-select .ant-select-selection-overflow'
				);

			if (selectorElement) {
				selectorElement.scrollTo({
					left: selectorElement.scrollWidth,
					behavior: 'smooth',
				});
			}
		}
	}, []);

	const debouncedScrollToEnd = useMemo(
		() => debounce(scrollToEnd, 100),
		[scrollToEnd]
	);

	useEffect(() => {
		if (
			valueFromProps &&
			Array.isArray(valueFromProps) &&
			valueFromProps.length > 0
		) {
			debouncedScrollToEnd();
		}
	}, [valueFromProps?.length, debouncedScrollToEnd]);

	useEffect(() => {
		if (!loading && options.length > 0) {
			const allCurrentOptions = options.flatMap((group) => group.options);
			const allHistoricOptions = historicOptions.flatMap(
				(group) => group.options || []
			);

			const historicMap = new Map();
			allHistoricOptions.forEach((opt) => {
				historicMap.set(opt.value, opt);
			});

			allCurrentOptions.forEach((opt) => {
				historicMap.set(opt.value, opt);
			});

			const allUniqueOptions = Array.from(historicMap.values());
			const subCompanies = [
				...new Set(
					allUniqueOptions.map((opt) => opt.claim?.subCompany || 'Other')
				),
			].sort();

			const newHistoricOptions = subCompanies.map((subCompany) => {
				const companyOptions = allUniqueOptions.filter(
					(opt) => (opt.claim?.subCompany || 'Other') === subCompany
				);

				return {
					label: subCompany,
					options: companyOptions,
				};
			});

			setHistoricOptions(newHistoricOptions);
		}
	}, [options, loading]);

	const handleChange = useCallback(
		(accountClaimId, fullClaim) => {
			if (onChange && accountClaimId) {
				onChange(accountClaimId);
				setFullValue(fullClaim);
			}
		},
		[onChange, setFullValue, options]
	);

	const OptionRenderer = useMemo(
		() =>
			({ option }) => (
				<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
					<Avatar
						size="small"
						style={{
							backgroundColor: option?.claim?.userId ? '#14d6d3' : '#aab3b3ff',
							fontSize: '12px',
							minWidth: 24,
							flexShrink: 0,
						}}
					>
						{option.initials}
					</Avatar>
					<div
						style={{
							display: 'flex',
							flexDirection: 'column',
							justifyContent: 'center',
							minWidth: 0,
							flex: 1,
						}}
					>
						<div
							style={{
								display: 'flex',
								alignItems: 'baseline',
								columnGap: '4px',
								flexWrap: 'wrap',
								lineHeight: '1.2',
							}}
						>
							<Text
								strong
								style={{
									fontSize: '13px',
									whiteSpace: 'nowrap',
									lineHeight: '1.2',
									maxWidth: 200,
								}}
								ellipsis={{
									tooltip: `${option.claim?.firstName || ''} ${option.claim?.lastName || ''}`,
								}}
							>
								{option.claim?.firstName} {option.claim?.lastName}
							</Text>
							{option.claim?.emailAddress && (
								<Text
									style={{
										fontSize: '11px',
										color: 'rgba(0, 0, 0, 0.45)',
										fontWeight: 200,
										lineHeight: '1.2',
										maxWidth: 200,
									}}
									ellipsis={{
										tooltip: option.claim?.emailAddress || '',
									}}
								>
									{option.claim?.emailAddress}
								</Text>
							)}
						</div>
						{option.claim?.title && (
							<Text
								style={{
									fontSize: '11px',
									color: 'rgba(0, 0, 0, 0.45)',
									fontWeight: 200,
									lineHeight: '1.2',
									maxWidth: 250,
								}}
								ellipsis={{
									tooltip: option.claim?.title || '',
								}}
							>
								{option.claim?.title}
							</Text>
						)}
					</div>
				</div>
			),
		[]
	);

	const selectOptions = useMemo(
		() =>
			options.map((group) => ({
				label: (
					<div
						style={{ color: 'var(--dove-gray)', fontWeight: 700, fontSize: 13 }}
					>
						{group.label}
					</div>
				),
				options: group.options.map((option) => ({
					...option,
					label: <OptionRenderer option={option} />,
				})),
			})),
		[options, OptionRenderer]
	);

	const tagRender = useCallback(
		(props) => {
			const { label, value = [], closable, onClose } = props;

			const allOptions = historicOptions.flatMap((group) => group.options);
			const foundOption = allOptions.find((opt) => opt.value === value);
			const initials = foundOption?.initials || '';

			return (
				<span
					style={{
						display: 'inline-flex',
						alignItems: 'center',
						padding: '2px 7px',
						margin: '0px 2px 0px',
						backgroundColor: 'var(--primary-btn-color, var(--theme-primary))',
						border: 'none',
						borderRadius: '12px',
						fontSize: '10px',
						fontWeight: '900',
						color: '#fff',
						flexShrink: 0,
						cursor: 'pointer',
					}}
				>
					<Tooltip title={foundOption?.label}>{initials}</Tooltip>
					{closable && (
						<span
							style={{
								marginLeft: '4px',
								cursor: 'pointer',
								fontSize: '10px',
								color: '#fff',
							}}
							onClick={onClose}
						>
							×
						</span>
					)}
				</span>
			);
		},
		[historicOptions]
	);

	const totalSelected = valueFromProps
		? Array.isArray(valueFromProps)
			? valueFromProps.length
			: 1
		: 0;

	return (
		<div style={{ position: 'relative', display: 'inline-block' }}>
			<style>
				{`
          .horizontal-tags-select .ant-select-selector {
            border-radius: 24px !important;
            padding-left: 0 !important;
            overflow-x: auto !important;
            overflow-y: hidden !important;
            white-space: nowrap !important;
            flex-wrap: nowrap !important;
            scrollbar-width: none !important;
            -ms-overflow-style: none !important;
          }
          
          .horizontal-tags-select .ant-select-selector::-webkit-scrollbar {
            display: none !important;
            padding: 0 !important;
          }
          
          .horizontal-tags-select .ant-select-selection-overflow {
            display: flex !important;
            flex-wrap: nowrap !important;
            overflow-x: auto !important;
            overflow-y: hidden !important;
            scrollbar-width: none !important;
            -ms-overflow-style: none !important;
            max-width: calc(100%) !important;
            scroll-behavior: smooth !important;
          }
          
          .horizontal-tags-select .ant-select-dropdown {
            will-change: transform !important;
          }
          
          .horizontal-tags-select .ant-select-item {
            will-change: transform !important;
          }
          
          .horizontal-tags-select .ant-select-selection-overflow::-webkit-scrollbar {
            display: none !important;
          }
          
          .horizontal-tags-select .ant-select-selection-overflow-item {
            flex-shrink: 0 !important;
            display: inline-flex !important;
          }
          
          .horizontal-tags-select .ant-select-selection-search {
            flex-shrink: 0 !important;
            min-width: 15px !important;
            align-self: center !important;
          }
          
          .horizontal-tags-select .ant-select-selection-search-input {
            min-width: 15px !important;
          }
        `}
			</style>
			<Select
				ref={selectRef}
				mode={mode}
				value={valueFromProps}
				bordered={false}
				placeholder={placeholder}
				className="horizontal-tags-select"
				onChange={(val, option) => {
					handleChange(val, option);
				}}
				onSearch={handleSearch}
				style={style}
				showSearch
				dropdownStyle={{
					maxWidth: '350px',
					minWidth: '350px',
					transform: 'translateZ(0)',
					backfaceVisibility: 'hidden',
				}}
				filterOption={false}
				virtual={true}
				loading={loading}
				onDropdownVisibleChange={(open) => setDropdownOpen(open)}
				getPopupContainer={() =>
					document.querySelector('.send-modal-container')
				}
				notFoundContent={
					loading ? (
						<Spin size="small" />
					) : error ? (
						'Error loading results'
					) : searchValue && options.length === 0 ? (
						'No results found'
					) : dropdownOpen && !searchValue ? (
						<div style={{ color: '#595959' }}>Search by name, email, or title</div>
					) : null
				}
				options={selectOptions}
				labelInValue
				tagRender={tagRender}
				{...props}
			/>
			{mode === 'multiple' && totalSelected > 0 && (
				<Badge
					count={`${totalSelected}`}
					style={{
						position: 'absolute',
						top: '-24px',
						right: '-6px',
						backgroundColor: 'var(--primary-btn-color, var(--theme-primary))',
						color: '#fff',
						fontSize: '11px',
						height: '19px',
						minWidth: '19px',
						lineHeight: '19px',
						padding: '0',
						borderRadius: '20px',
						border: 'none',
						boxShadow: '0 0 0 1px #fff',
					}}
				/>
			)}
		</div>
	);
};

export default AccountClaimsSelect;
export { AccountClaimsSelect, useAccountClaimsSelect };
